import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { isDesktopApp } from '@/desktop/electronApi';
import { useDesktopSessionStore } from '@/desktop/desktopSessionStore';
import { useSyncStore } from '@/store/syncStore';
import {
  companyDb,
  ensureDeviceId,
  isLocalFirstMode,
  loadSyncModeFromSettings,
  maybeAutoSync,
  refreshQueueCounts,
  runChunkedCatalogSync,
  startConnectivityMonitor,
} from '@/offline';

/**
 * Mount once when authenticated: connectivity, sync mode, optional bootstrap, Mode 2 auto-drain.
 */
export function useOfflineInit() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const companyCode = useAuthStore((s) => s.company?.code);
  const pendingDesktopSetup = useDesktopSessionStore((s) => s.pendingDesktopSetup);
  const posSyncMode = useSyncStore((s) => s.posSyncMode);
  const isServerReachable = useSyncStore((s) => s.isServerReachable);
  const catalogSyncOpen = useSyncStore((s) => s.catalogSyncProgress.open);
  const qc = useQueryClient();
  const bootstrappedForMode = useRef<string | null>(null);
  // Only Electron setup wizard owns catalog; ignore persisted flag on web.
  const wizardOwnsCatalog = isDesktopApp() && pendingDesktopSetup;

  useEffect(() => {
    if (!isAuthenticated || !companyCode) return;
    const stop = startConnectivityMonitor();
    void (async () => {
      await ensureDeviceId();
      const mode = await loadSyncModeFromSettings();
      await refreshQueueCounts();
      // Desktop setup wizard owns the first catalog download — avoid racing it.
      if (wizardOwnsCatalog) return;
      if (isLocalFirstMode(mode)) {
        await ensureLocalCatalog(qc, mode === 'Offline Always Manual Sync');
        if (mode === 'Local First Auto Sync') {
          void maybeAutoSync();
        }
      }
    })();
    return () => {
      stop?.();
    };
  }, [isAuthenticated, companyCode, qc, wizardOwnsCatalog]);

  // When admin switches into a local mode, seed/refresh catalog (with popup for empty Offline Always)
  useEffect(() => {
    if (!isAuthenticated || !companyCode) return;
    // Setup wizard runs its own runChunkedCatalogSync — do not start a second one.
    if (wizardOwnsCatalog) return;
    if (!isLocalFirstMode(posSyncMode)) {
      bootstrappedForMode.current = null;
      return;
    }
    if (catalogSyncOpen) return;
    if (bootstrappedForMode.current === posSyncMode) return;

    void (async () => {
      const count = await companyDb().catalog_items.count();
      // Offline Always with empty catalog: force full chunked download + popup
      const force = posSyncMode === 'Offline Always Manual Sync' && count === 0;
      if (count === 0 || force) {
        await ensureLocalCatalog(qc, true);
      }
      bootstrappedForMode.current = posSyncMode;
    })();
  }, [posSyncMode, isAuthenticated, companyCode, qc, catalogSyncOpen, wizardOwnsCatalog]);

  // Local First: whenever server becomes reachable again, drain the offline queue.
  useEffect(() => {
    if (!isAuthenticated || !companyCode) return;
    if (posSyncMode !== 'Local First Auto Sync') return;
    if (!isServerReachable) return;
    void maybeAutoSync();
  }, [isServerReachable, posSyncMode, isAuthenticated, companyCode]);
}

async function ensureLocalCatalog(qc: ReturnType<typeof useQueryClient>, withPopup: boolean) {
  const hasItems = await companyDb().catalog_items.count();
  if (hasItems > 0 && !withPopup) return;
  if (!useSyncStore.getState().isServerReachable && !navigator.onLine) return;
  if (useSyncStore.getState().catalogSyncProgress.open) return;
  try {
    if (withPopup || hasItems === 0) {
      await runChunkedCatalogSync({ force: true });
    }
    qc.invalidateQueries({ queryKey: ['posItems'] });
    qc.invalidateQueries({ queryKey: ['posCustomers'] });
    qc.invalidateQueries({ queryKey: ['posCategories'] });
    qc.invalidateQueries({ queryKey: ['items'] });
    qc.invalidateQueries({ queryKey: ['customers'] });
  } catch {
    // Bootstrap can fail offline; continue with empty catalog
  }
}
